import React, { useEffect, useState } from "react"

import { Form, Input, Modal, Select, Spin } from "antd"

import { stockFBSMarketApi } from "../../../common/api/endpoints/stockFBSMarketApi"
import { handleResponseError } from "../../../common/api/middleware"
import { TAppDispatch } from "../../../redux/store"
import { OzonCancelReason } from "../../../types/api"

export type CancelOrderState = {
  visible: boolean
  orderIds: string[]
  marketplaceId: number | null
}

type Props = {
  state: CancelOrderState
  setState: React.Dispatch<React.SetStateAction<CancelOrderState>>
  /** Отменяет заказы с выбранной причиной */
  onConfirm: (orderIds: string[], marketplaceId: number, reasonId: number, message: string) => void
}

/**
 * Отмена заказов OZON: маркетплейс требует причину из своего списка,
 * а для части причин ещё и пояснение текстом. Причины запрашиваются под
 * конкретные отправления — доступные варианты у разных заказов различаются.
 */
const CancelOrderModal = ({ state, setState, onConfirm }: Props) => {
  const [ form ] = Form.useForm()
  const [ reasons, setReasons ] = useState<OzonCancelReason[]>([])
  const [ isLoading, setIsLoading ] = useState(false)

  const selectedReasonId = Form.useWatch("cancel_reason_id", form)
  const selectedReason = reasons.find((reason) => reason.id === selectedReasonId)

  const closeModal = () => {
    form.resetFields()
    setReasons([])
    setState({ visible: false, orderIds: [], marketplaceId: null })
  }

  useEffect(() => {
    if (!state.visible || state.orderIds.length === 0) {
      return
    }

    const fetchReasons = async () => {
      try {
        setIsLoading(true)
        const { data } = await stockFBSMarketApi.fetchOzonCancelReasons(state.orderIds)
        const items = (data ?? []) as OzonCancelReason[]
        setReasons(items)
        // Первая причина подставляется сразу, чтобы не заставлять выбирать вручную
        if (items.length > 0) {
          form.setFieldValue("cancel_reason_id", items[0].id)
        }
      } catch (error: any) {
        const dispatch = (() => {}) as TAppDispatch
        handleResponseError(dispatch, error)
        closeModal()
      } finally {
        setIsLoading(false)
      }
    }

    fetchReasons()
  }, [ state.visible, state.orderIds ])

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (!state.marketplaceId) {
        return
      }

      onConfirm(
        state.orderIds,
        state.marketplaceId,
        values.cancel_reason_id,
        values.cancel_reason_message ?? ""
      )
      closeModal()
    })
  }

  return (
    <Modal
      title={state.orderIds.length > 1 ? `Отмена заказов (${state.orderIds.length})` : "Отмена заказа"}
      open={state.visible}
      onOk={handleOk}
      onCancel={closeModal}
      okText="Отменить заказ"
      cancelText="Закрыть"
      okButtonProps={{ danger: true, disabled: isLoading || reasons.length === 0 }}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        <Form form={form} layout="vertical">
          <Form.Item
            label="Причина отмены"
            name="cancel_reason_id"
            rules={[ { required: true, message: "Выберите причину отмены" } ]}
          >
            <Select
              options={reasons.map((reason) => ({ value: reason.id, label: reason.name }))}
              placeholder="Причина отмены"
            />
          </Form.Item>
          {selectedReason?.needs_message && (
            <Form.Item
              label="Пояснение"
              name="cancel_reason_message"
              rules={[ { required: true, message: "Для этой причины OZON требует пояснение" } ]}
            >
              <Input.TextArea rows={3} placeholder="Опишите причину отмены" />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  )
}

export default CancelOrderModal
